import { downloadFile } from '#helper/download-file';

import i18n from '#locale';

import { createApi, baseQuery, enhanceEndpoints } from './extension';
import type * as statsAtrevdsIdGet from './statistics/atrevds/{id}/get';
import type * as advertStatusPut from './statistics/atrevds/put';
import type * as fullStatXlsxGet from './fullstat/get';
import type * as supplierSubjectGet from './supplier-subjects/get';
import type * as campaignPlacementGet from './placement/get';
import type * as placementPut from './placement/put';
import type * as budgetGet from './budget/get';
import type * as budgetPost from './budget/post';
import type * as renameCampaignPost from './rename-campaign/post';
import type * as editCampaignStatusGet from './edit-campaign-status/get';
import type * as searchCatalogStatWordsGet from './seacat/{id}/stat-words/get';
import type * as searchCatalogSetPlusGet from './seacat/{id}/set-plus/get';
import type * as searchCatalogSetPlusPost from './seacat/{id}/set-plus/post';
import type * as searchCatalogSetExcludedPost from './seacat/{id}/set-excluded/post';
import type * as searchCatalogSetStrongPost from './seacat/{id}/strong/post';
import type * as searchCatalogSetPhrasePost from './seacat/{id}/phrase/post';
import type * as updXlsxGet from './updxlsx/get';
import type * as statsXlsxGet from './stats/get';
import type * as paymentsxlsxGet from './paymentsxlsx/get';

export const api = createApi({
    reducerPath: 'api/main',
    baseQuery  : baseQuery('/api'),
    tagTypes   : ['seacat-placement', 'seacat-new-items', 'placement', 'edit-status', 'advert-status', 'placement', 'balance', 'stats-atrevd-id'],
    endpoints  : (build) => ({
        statsAtrevdsIdGet: build.query<statsAtrevdsIdGet.TResponse, statsAtrevdsIdGet.IParameters>({
            query: (params) => ({
                method: 'GET',
                url   : '/v1/atrevd',
                params: {
                    'advert-id': params.path.id
                }
            }),
            providesTags: (response, error, params) => {
                if(params.path.id) {
                    return [{
                        type: 'stats-atrevd-id',
                        id  : Number(params.path.id)
                    }];
                }

                return ['stats-atrevd-id'];
            }
        }),
        advertStatusPut: build.mutation<undefined, advertStatusPut.IParameters>({
            query: ({ path }) => ({
                method: 'PUT',
                url   : `/v1/atrevd/${path.id}/to-${path.status}`
            }),
            invalidatesTags: (response, error, params) => {
                if(params.path.id) {
                    return ['advert-status', {
                        type: 'stats-atrevd-id',
                        id  : Number(params.path.id)
                    }];
                }

                return ['advert-status', 'stats-atrevd-id'];
            }
        }),
        fullStatXlsxGet: build.query<fullStatXlsxGet.TResponse, fullStatXlsxGet.IParameters>({
            query: (params) => ({
                method: 'GET',
                url   : '/v3/fullstat',
                params: {
                    advertID: params?.query?.advertID,
                    appType : params?.query?.appType,
                    from    : params?.query?.from,
                    to      : params?.query?.to
                },
                responseHandler: async (response) => (
                    downloadFile(
                        response, i18n.t('routes.statistics-details.file-name', {
                            from    : params?.query?.from,
                            to      : params?.query?.to,
                            advertID: params?.query?.advertID
                        })
                    )
                ),
                cache: 'no-cache'
            })
        }),
        autoSupplierSubjectsGet: build.query<supplierSubjectGet.TResponse, void>({
            query: () => ({
                method: 'GET',
                url   : 'v2/auto/supplier-subjects'
            })
        }),
        campaignPlacementGet: build.query<campaignPlacementGet.TResponse, campaignPlacementGet.IParameters>({
            providesTags: ['placement'],
            query       : (params) => ({
                method: 'GET',
                url   : `/v2/${params.path.type}/${params.path.id}/placement`
            })
        }),
        budgetGet: build.query<budgetGet.TResponse, budgetGet.IParameters>({
            query: ({ path }) => ({
                method: 'GET',
                url   : `/v2/${path.type}/${path.id}/budget`
            })
        }),
        budgetPost: build.mutation<undefined, budgetPost.IParameters>({
            query: ({ path, body }) => ({
                method: 'POST',
                url   : `/v2/${path.type}/${path.id}/budget/${path.direction}`,
                body
            })
        }),
        renameCampaignPost: build.mutation<undefined, renameCampaignPost.IParameters>({
            query: ({ path, body }) => ({
                method: 'POST',
                url   : '/v3/rename',
                params: { 'advert-id': path.id },
                body
            })
        }),
        editCampaignStatusGet: build.mutation<number, editCampaignStatusGet.IParameters>({
            query: ({ path }) => ({
                method: 'GET',
                url   : `/v2/${path.type}/${path.id}/${path.status}`
            }),
            invalidatesTags: (response, error, params) => {
                if(params.path.id) {
                    return ['edit-status', {
                        type: 'stats-atrevd-id',
                        id  : Number(params.path.id)
                    }];
                }

                return ['edit-status', 'stats-atrevd-id'];
            }
        }),
        placementPut: build.mutation<undefined, placementPut.IParameters>({
            query: ({ path }) => ({
                method: 'PUT',
                url   : `/v2/${path.type}/${path.id}/placement`
            }),
            invalidatesTags: (response, error, params) => {
                if(params.path.id) {
                    return [{
                        type: 'stats-atrevd-id',
                        id  : Number(params.path.id)
                    }];
                }

                return ['stats-atrevd-id'];
            }
        }),
        searchCatalogStatWordsGet: build
            .query<searchCatalogStatWordsGet.TResponse, searchCatalogStatWordsGet.IParameters>({
            query: (params) => ({
                method: 'GET',
                url   : `/v2/seacat/${params.path.advertID}/stat-words`
            })
        }),
        searchCatalogFixedSwitchGet: build
            .mutation<searchCatalogSetPlusGet.TResponse, searchCatalogSetPlusGet.IParameters>({
            query: (params) => ({
                method: 'GET',
                url   : `/v2/seacat/${params?.query?.id}/set-plus`,
                params: {
                    fixed: params?.query?.fixed
                }
            })
        }),
        searchCatalogSetPluseWordsPost: build.mutation<undefined, searchCatalogSetPlusPost.IParameters>({
            query: ({ path, body }) => ({
                method: 'POST',
                url   : `/v2/seacat/${path.id}/set-plus`,
                body
            })
        }),
        searchCatalogSetStrongWordsPost: build.mutation<undefined, searchCatalogSetStrongPost.IParameters>({
            query: ({ path, body }) => ({
                method: 'POST',
                url   : `/v2/seacat/${path.id}/strong`,
                body
            })
        }),
        searchCatalogSetPhraseWordsPost: build.mutation<undefined, searchCatalogSetPhrasePost.IParameters>({
            query: ({ path, body }) => ({
                method: 'POST',
                url   : `/v2/seacat/${path.id}/phrase`,
                body
            })
        }),
        searchCatalogSetExcludedWordsPost: build.mutation<undefined, searchCatalogSetExcludedPost.IParameters>({
            query: ({ path, body }) => ({
                method: 'POST',
                url   : `/v2/seacat/${path.advertID}/set-excluded`,
                body
            })
        }),
        updXlsxGet: build.query<updXlsxGet.TResponse, updXlsxGet.IParameters>({
            query: (params) => ({
                method: 'GET',
                url   : '/v5/updxlsx',
                params: {
                    from: params.query.from,
                    to  : params.query.to,
                    type: params.query.type
                },
                responseHandler: async (response) => (
                    downloadFile(
                        response, i18n.t('routes.finances.file-name', {
                            typeLocalized: params?.query?.typeLocalized,
                            from         : params?.query?.from,
                            to           : params?.query?.to
                        })
                    )
                ),
                cache: 'no-cache'
            })
        }),
        statsXlsxGet: build.query<statsXlsxGet.TResponse, void>({
            query: () => ({
                method         : 'GET',
                url            : '/v3/stats/xls',
                responseHandler: async (response) => (
                    downloadFile(response, i18n.t('routes.statistics.file-name'))
                ),
                cache: 'no-cache'
            })
        }),
        paymentsXlsxGet: build.query<paymentsxlsxGet.TResponse, paymentsxlsxGet.IParameters>({
            query: (params) => ({
                method: 'GET',
                url   : '/v5/paymentsxlsx',
                params: {
                    'from_date': params.query?.from_date,
                    'to_date'  : params.query?.to_date,
                    'type'     : params.query?.type
                },
                responseHandler: async (response) => (
                    downloadFile(response, i18n.t('routes.finances.top-up-history.file-name', {
                        typeLocalized: params?.query?.typeLocalized,
                        dateLocalized: params?.query?.dateLocalized
                    }))
                ),
                cache: 'no-cache'
            })
        })
    })
});
enhanceEndpoints(api);

export default api;
